import { FormikProps } from "formik";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getBillingDetails,
  getLoginDetailsByID,
} from "../myprofile/services/myprofile.services";
import Loading from "../../components/spinner/Loading";
import { ErrorToast } from "../../toast/Toasts";
import { handleUppercaseChange } from "../../_helpers/formatUtils";
import { IReviewBookingDetails } from "./reviewbookingModel";
import { FormField } from "../../components/formfield/FormField";
import { State } from "country-state-city";

interface ContactDetailsFormProps {
  formik: FormikProps<IReviewBookingDetails>;
  isGSTRequired: boolean;
  isGSTApplicable: boolean;
  fieldRefs: React.MutableRefObject<any>;
}

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  formik,
  isGSTRequired,
  isGSTApplicable,
  fieldRefs,
}) => {
  const dispatch = useAppDispatch();
  const billingLoading = useAppSelector(
    (state) => state.profile.billingLoading
  );
  const loginLoading = useAppSelector((state) => state.profile.loginLoading);
  const billingData = useAppSelector((state) => state.profile.billingData);
  const loginData = useAppSelector((state) => state.profile.loginData);

  useEffect(() => {
    if (isGSTRequired) {
      formik.setFieldValue("isGST", true);
    }
  }, [isGSTRequired]);

  useEffect(() => {
    if (Object.keys(billingData).length === 0 && formik.values.isGST) {
      dispatch(
        getBillingDetails({ user_guid: localStorage.getItem("user_guid") })
      );
    }
    if (Object.keys(loginData).length === 0) {
      dispatch(
        getLoginDetailsByID({ user_guid: localStorage.getItem("user_guid") })
      );
    }
  }, [formik.values.isGST]);

  useEffect(() => {
    if (Object.keys(loginData).length !== 0) {
      formik.setFieldValue("email_id", loginData?.email_id);
      formik.setFieldValue("mobile_no", loginData?.mobile_no);
    } else {
      formik.setFieldValue("email_id", "");
      formik.setFieldValue("mobile_no", "");
    }
  }, [loginData]);

  useEffect(() => {
    if (Object.keys(billingData).length !== 0) {
      formik.setFieldValue("gst_number", billingData?.billing_gst_no);
      formik.setFieldValue(
        "gst_registered_name",
        billingData?.billing_gst_reg_name
      );
      formik.setFieldValue("gst_address", billingData?.billing_address);
      formik.setFieldValue("state_id", billingData?.billing_state_code);
    } else {
      formik.setFieldValue("gst_number", "");
      formik.setFieldValue("gst_registered_name", "");
      formik.setFieldValue("gst_address", "");
      formik.setFieldValue("state_id", "");
    }
  }, [billingData, formik.values.isGST]);

  const renderFormField = (
    name: keyof IReviewBookingDetails,
    label: string,
    type: string,
    placeholder: string,
    required = false,
    uppercase = false,
    additionalProps: any = {}
  ) => {
    const touched = formik.touched[name];
    const error = formik.errors[name];
    const value = formik.values[name];

    return (
      <FormField
        label={label}
        required={required}
        error={error as string}
        touched={touched}
        className="w-100"
      >
        <input
          type={type}
          className={touched && error ? "error-border" : ""}
          placeholder={placeholder}
          name={name}
          onChange={
            uppercase
              ? (e) => handleUppercaseChange(formik, e)
              : formik.handleChange
          }
          onBlur={formik.handleBlur}
          value={value as string | number | readonly string[] | undefined}
          // ref={(el) => (fieldRefs.current[name] = el)}
          {...additionalProps}
        />
      </FormField>
    );
  };

  return (
    <div className="adult-form">
      <div className="child-form">
        <h5>Booking details will be sent to</h5>
        <div className="form-1 form">
          <form>
            <div className="position-relative">
              <Loading loading={loginLoading} />
              <div className="field-set">
                {renderFormField(
                  "email_id",
                  "Email",
                  "email",
                  "Email Address",
                  true,
                  false
                )}
                {renderFormField(
                  "mobile_no",
                  "Mobile No",
                  "number",
                  "Mobile No",
                  true,
                  false,
                  {
                    onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = Math.max(0, parseInt(e.target.value))
                        .toString()
                        .slice(0, 10);
                    },
                  }
                )}
              </div>
            </div>
            <div className={`chaeckbox ${formik.values.isGST ? "pb-0" : ""}`}>
              <input
                type="checkbox"
                name="isGST"
                checked={formik.values.isGST as boolean}
                onChange={(e) => {
                  if (isGSTApplicable) {
                    formik.handleChange(e);
                  } else {
                    ErrorToast("Sorry! GST is not allowed for this booking.");
                  }
                }}
              />
              <label>
                <strong>I have a GST number</strong>
              </label>
            </div>
            {formik.values.isGST && (
              <div className="position-relative">
                <Loading loading={billingLoading} />
                <div className="field-set align-end mb-3">
                  {renderFormField(
                    "gst_number",
                    "GST No",
                    "text",
                    "",
                    false,
                    true
                  )}
                  {renderFormField(
                    "gst_registered_name",
                    "Registered Name",
                    "text",
                    "",
                    false,
                    true
                  )}
                </div>
                <div className="field-set align-end mb-3">
                  {renderFormField(
                    "gst_address",
                    "Address",
                    "text",
                    "",
                    false,
                    true
                  )}
                  <FormField
                    label={"State"}
                    error={formik.errors["state_id"] as string}
                    touched={formik.touched["state_id"]}
                    className="w-100"
                  >
                    <select
                      name="state_id"
                      value={formik.values.state_id}
                      onChange={(e) => {
                        const code = e?.target?.value;
                        const selectedState = State.getStatesOfCountry(
                          "IN"
                        ).find((state) => state?.isoCode === code);
                        const name = selectedState ? selectedState?.name : "";
                        formik.setFieldValue("state_id", code);
                        formik.setFieldValue("state_name", name);
                      }}
                      className={
                        formik.touched["state_id"] && formik.errors["state_id"]
                          ? "error-border"
                          : ""
                      }
                      // onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select State</option>
                      {State?.getStatesOfCountry("IN")?.map((e: any) => (
                        <option key={e?.isoCode} value={e?.isoCode}>
                          {e?.name}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsForm;
